import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { Flex, Text } from 'rebass/styled-components';

import AddEditContact from '../AddEditContact';
import useContactColumns from './useContactColumns';

import { Table, SearchInput } from 'ui/patterns';
import { Button, Stack } from 'ui/components';

import { useGetContactsList } from 'hooks';

import * as Icon from 'assets/icons';

const sortItem = { sortKey: 'Name', sortOrder: 'asc' };

export default function ContactList(): ReactElement {
  const [showPanel, setShowPanel] = useState(false);
  const [contactId, setContactId] = useState(0);
  const [columnsSearch, setColumnsSearch] = useState({}); // table header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [keywordSearch, setKeywordSearch] = useState(''); //keyword search
  const [mode, setMode] = useState('');

  const params = useParams<{
    accountId: string;
    tabId?: string;
  }>();
  const accountId = parseInt(params.accountId);
  const tbodyRef = useRef() as React.MutableRefObject<HTMLTableSectionElement>;

  const columns = useContactColumns();

  const {
    data: contactList,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useGetContactsList(
    accountId,
    columnsSearch,
    keywordSearch,
    currentSortItem,
  );

  const tableData = useMemo(() => {
    if (contactList) {
      return contactList?.pages?.map((dataItem: any) => dataItem.Data).flat();
    }
  }, [contactList]);

  const totalItems = useMemo(() => {
    if (contactList) {
      return contactList.pages[0].FilteredRecords;
    }
    return 0;
  }, [contactList]);

  const handleScroll = useCallback(
    (e: any) => {
      if (tbodyRef.current === e.target) {
        const bottom =
          e.target.clientHeight - 10 <
            e.target.scrollHeight - e.target.scrollTop &&
          e.target.scrollHeight - e.target.scrollTop <
            e.target.clientHeight + 10;
        if (bottom) {
          hasNextPage && fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handleScroll]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const togglePanel = () => {
    setShowPanel(false);
    setContactId(0);
  };

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
  };

  const handleEditContact = (row: any): void => {
    setContactId(row.original.Id);
    setShowPanel(true);
    setMode('Edit');
  };

  const onEditPress = (id: number): void => {
    setContactId(id);
    setShowPanel(true);
    setMode('Edit');
  };

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Text fontSize={1} color="text.body" sx={{ height: '20px' }}>
          {tableData &&
            tableData.length > 0 &&
            `Showing ${tableData?.length} of ${totalItems}
          ${isFetching ? 'Processing' : ''}`}
        </Text>
        <Stack direction="x" gap={2}>
          <SearchInput
            placeholder="Search Contacts"
            handleChange={handleKeywordSearch}
            value={keywordSearch}
          />
          <Button
            iconBefore={<Icon.Refresh title="" ariaHidden="true" />}
            variant="secondary"
            onClick={handleReset}
          >
            Reset All
          </Button>
          <Button
            iconBefore={<Icon.Add title="" ariaHidden="true" />}
            variant="primary"
            onClick={() => {
              setContactId(0);
              setShowPanel(true);
              setMode('Add');
            }}
          >
            Add New Contact
          </Button>
        </Stack>
      </Flex>
      <Table
        columns={columns}
        data={tableData ? tableData : undefined}
        isLoading={isLoading || isFetching}
        headerUpdateCallBack={headerUpdateCallBack}
        handleSortChange={handleSortChange}
        sortedItem={currentSortItem}
        columnsSearch={columnsSearch}
        onRowDoubleClick={handleEditContact}
        onEditPress={onEditPress}
        onEndReached={onEndReached}
        alignVertical={true}
      />
      <AddEditContact
        showPanel={showPanel}
        togglePanel={togglePanel}
        contactId={contactId}
        accountId={accountId}
        mode={mode}
      />
    </>
  );
}
